import React from 'react'
import { observer } from 'mobx-react'
import { observable, toJS } from 'mobx'
import dayjs from 'dayjs'
import {
  i18nTranslate,
  getNamefromFirstname,
  isFeatureEnabled,
  isSepaPaymentRecord,
  isSepaMandateInProgress,
  isGPSEnabled,
  getDefaultPaymentMethod,
  getGpsRoutedCreditCardOption,
  getGpsCreditCardPaymentIdentity,
  resolveGpsPaymentName,
} from 'src/utils'
import { Modal, Button, Tabs, Tab, Col } from 'react-bootstrap'
import {
  storeContainer,
  addressContainer,
  customerContainer,
  checkoutContainer,
  tokenExContainer,
  paymetricContainer,
} from 'src/models'
import {
  getAddressFieldsJsonBasedOnCountry,
  getAddressOnlyFieldsForValidation,
  checkIfTwoFormValuesAreEqual,
  getValidationErrorMessage,
} from 'src/deps'
import {
  AddressValidation,
  toastState,
  PaymentIcon,
  overlayState,
  PaymentMarketForm,
  ImageComponent,
} from 'src/views/components'
import { PaymentDeleteModal } from './PaymentDeleteModal'
import { SubscriptionPaymentEditModal } from './SubscriptionPaymentEditModal'
import { CreditCardExpiryInfo } from 'src/views/components/FlexibleSubscription/ShippingHeader/CreditCardExpiryInfo'
import {
  getSubscriptionPaymentPayloadData,
  getSepaIconURL,
} from 'src/utils/paymentContainerHelper'
import { APPConfig, convertToBoolean } from 'config/appConfig'
import { SepaMandateWarningBanner } from 'src/views/components/SepaMandateWarningBanner'
import './styles.scss'

@observer
class SubscriptionPaymentModal extends React.Component {
  @observable activeTab = 'savedDetailsTab'
  @observable selectedPaymentId = 0
  @observable selectedAddPaymentType = getDefaultPaymentMethod() || 'creditcard'
  @observable isToShowSuggestionModal = false
  @observable isAddressInvalid = false
  @observable suggestionBillingAddressList = []
  @observable paymentFormData = {}
  @observable isToShowDeleteConfirmationModal = false
  @observable selectedPaymentForDelete = {}
  @observable isToShowPaymentEditModal = false
  @observable selectedPaymentDataToEdit = {}
  @observable isGPSPaymentSaveInProgress = false
  @observable gpsSaveStatusMessage = ''
  @observable mandateWarningPaymentId = ''

  /**
   * @description Returns paymetric integration flag
   * @returns {Boolean}
   */
  enableDirectPaymetricAndAccertifyIntegration = () =>
    isFeatureEnabled('enableDirectPaymetricAndAccertifyIntegration')

  /**
   * @description Returns the appropriate payment container based on Paymetric flag
   * @returns {Object} tokenExContainer or paymetricContainer
   */
  getPaymentContainer = () => {
    return this.enableDirectPaymetricAndAccertifyIntegration()
      ? paymetricContainer
      : tokenExContainer
  }

  componentDidMount = () => {
    this.selectedPaymentId = this.props?.selectedPaymentId || 0
  }

  getFormFieldsBasedOnCountry = async () => {
    let paymentFormFieldJson = await getAddressFieldsJsonBasedOnCountry(
      storeContainer?.defaultShippingRegion
    )
    let formFields = await getAddressOnlyFieldsForValidation(
      paymentFormFieldJson,
      'subscription'
    )
    return formFields
  }

  handlePaymentModalToggleView = () => {
    this.props.toggleSubscriptionPaymentModal()
  }

  callAddNewSubscriptionPaymentHandler = (addedPaymentId = '') => {
    const { handleAddNewSubscriptionPayment = function () {} } = this.props
    handleAddNewSubscriptionPayment(addedPaymentId)
  }

  handlePaymentTabChange = (tabName = '') => {
    this.activeTab = tabName
    if (tabName === 'addNewDetailsTab') {
      this.props.onAddNewPaymentTabSelected?.()
    }
  }

  handlePaymentEditButton = (id = '') => {
    const paymentData = customerContainer.paymentsForDashboard?.payments || []
    const selectedPayment =
      paymentData?.find(payment => payment?.id == id) || {}
    this.isToShowPaymentEditModal = true
    this.selectedPaymentDataToEdit = selectedPayment || {}
  }

  showDeleteConfirmationModal = (id = '') => {
    const paymentData = customerContainer.paymentsForDashboard?.payments || []
    const selectedPayment =
      paymentData?.find(payment => payment?.id == id) || {}
    this.selectedPaymentForDelete = selectedPayment
    this.isToShowDeleteConfirmationModal = true
  }

  handleDeleteConfirmationModalClose = (id = '') => {
    this.selectedPaymentForDelete = {}
    this.isToShowDeleteConfirmationModal = false
  }

  // Prefer the subscription-scoped response passed down from the dashboard
  // (Subscription category) over the shared checkoutContainer singleton, which
  // can be overwritten by an unrelated OneTime-category GPS call elsewhere
  // (e.g. the backend only returns Wire for the Subscription category).
  getGlobalPaymentResponse = () => {
    const subscriptionGlobalPaymentResponse = this.props?.globalPaymentResponse
    return subscriptionGlobalPaymentResponse &&
      Object.keys(subscriptionGlobalPaymentResponse).length > 0
      ? subscriptionGlobalPaymentResponse
      : checkoutContainer?.globalPaymentResponse || {}
  }

  getGPSCreditCardOptions = () => {
    return this.getGlobalPaymentResponse()?.CREDITCARD || []
  }

  getConfiguredGPSPaymentOption = () => {
    const gpsCreditCardOptions = this.getGPSCreditCardOptions()
    const configuredProcessorName =
      APPConfig.getAppConfig()?.gpsSavedCardProcessorName

    // Same fallback as the wallet, so a GPS market without a configured processor
    // name still saves the card as GPS instead of a plain CREDITCARD
    return getGpsRoutedCreditCardOption(
      gpsCreditCardOptions,
      configuredProcessorName
    )
  }

  handleAddNewPaymentApi = async (
    paymentData = {},
    isDefault = false,
    isDefaultSubscriptionPayment = false
  ) => {
    const addPaymentApiResponse = await customerContainer.addPayment(
      paymentData,
      true,
      false
    )
    const addedPaymentId = customerContainer.lastPaymentResponse?.id || ''
    const {
      isGPS3DSEnabledInSubscription = false,
      triggerSubscription3DSInitiateCall = async () => null,
    } = this.props
    if (customerContainer.isSuccessResponse(addPaymentApiResponse)) {
      if (isGPS3DSEnabledInSubscription && addedPaymentId) {
        const initiateResponse =
          await triggerSubscription3DSInitiateCall(addedPaymentId)

        // If 3DS initiate was attempted and failed, the helper already handles cleanup
        // (including delete on failure), so we must stop here and not run remaining checks.
        if (initiateResponse !== null && !initiateResponse?.redirectUrl) {
          return
        }

        if (initiateResponse?.redirectUrl) {
          return
        }
      }
      if (isDefault || isDefaultSubscriptionPayment) {
        this.callAddNewSubscriptionPaymentHandler(addedPaymentId)
      }
    }
  }

  handleAddressSuggestionModalContinue = async (formData = {}) => {
    const {
      cardFirstName = '',
      cardNumber = '',
      expiryDate = '',
      provider = '',
      paymentMethodType = 'CREDITCARD',
      setToDefaultPayment = false,
      setDefaultSubscriptionPayment = false,
    } = this.paymentFormData

    const expiryMonth = expiryDate?.split?.('/')?.[0] || '01'
    const expiryYear = expiryDate?.split?.('/')?.[1] || '20'

    const gpsPaymentOption = this.getConfiguredGPSPaymentOption()
    const { type: paymentTypeForRequest, name: paymentNameForRequest } =
      getGpsCreditCardPaymentIdentity(
        this.getGPSCreditCardOptions(),
        APPConfig.getAppConfig()?.gpsSavedCardProcessorName,
        paymentMethodType
      )

    const { properties, provider: normalizedProvider } =
      getSubscriptionPaymentPayloadData({
        paymentContainer: this.getPaymentContainer(),
        paymetricContainer,
        provider,
        gpsPaymentOption,
      })
    const reqParams = {
      type: paymentTypeForRequest,
      ...(paymentNameForRequest && {
        name: paymentNameForRequest,
      }),
      // Both flags are read at the top level, as in handleAddNewPaymentContinueButton.
      isDefault: setToDefaultPayment,
      isDefaultSubscriptionPayment: setDefaultSubscriptionPayment,
      accountInfo: {
        name: cardFirstName,
        lastName: cardFirstName,
        cardNumber,
        expiryMonth,
        expiryYear: '20' + expiryYear,
        provider: normalizedProvider,
        isDefault: setToDefaultPayment,
      },
      billingAddress: {
        ...formData,
      },
      properties,
    }

    if (formData?.selectedIndex <= 0) {
      let validateOriginalAddressApiResponse =
        await addressContainer.originalAddressApiCall(formData)
      this.handleSuggestionModalClose()
      if (
        addressContainer.isSuccessResponse(validateOriginalAddressApiResponse)
      ) {
        this.handlePaymentModalToggleView()
        this.handleAddNewPaymentApi(
          reqParams,
          setToDefaultPayment,
          setDefaultSubscriptionPayment
        )
      } else {
        const errorMessageToDisplay = getValidationErrorMessage(
          validateOriginalAddressApiResponse
        )
        toastState.setToastMessage(errorMessageToDisplay)
      }
    } else {
      this.handleSuggestionModalClose()
      this.handlePaymentModalToggleView()
      this.handleAddNewPaymentApi(
        reqParams,
        setToDefaultPayment,
        setDefaultSubscriptionPayment
      )
    }
  }

  handleSuggestionModalClose = () => {
    this.isToShowSuggestionModal = false
  }

  handleAddNewPaymentContinueButton = async (cardDetails = {}) => {
    const {
      cardFirstName = '',
      cardNumber = '',
      expiryDate = '',
      provider = '',
      setDefaultSubscriptionPayment = false,
      setToDefaultPayment = false,
      billingSameAsShipping = false,
      paymentMethodType = '',
      ...addressParams
    } = cardDetails

    this.paymentFormData = cardDetails
    const { selectedAddressId = 0 } = this.props
    const addressData = addressContainer.addressData || []

    let selectedSubscriptionAddress =
      addressData?.filter(
        addressInfo => addressInfo?.addressId === selectedAddressId
      ) || []
    const selectedBillingAddress =
      billingSameAsShipping === true && selectedSubscriptionAddress?.length > 0
        ? selectedSubscriptionAddress?.[0]
        : addressParams

    selectedBillingAddress.email =
      customerContainer?.profileResponse?.email || ''
    selectedBillingAddress.addressLine1 =
      selectedBillingAddress?.street1 ||
      selectedBillingAddress?.addressLine1 ||
      ''
    selectedBillingAddress.addressLine2 =
      selectedBillingAddress?.street2 ||
      selectedBillingAddress?.addressLine2 ||
      ''
    selectedBillingAddress.stateList =
      selectedBillingAddress?.state || selectedBillingAddress?.stateList || ''
    selectedBillingAddress.zip =
      selectedBillingAddress?.zipCode || selectedBillingAddress?.zip || ''
    delete selectedBillingAddress?.validated
    delete selectedBillingAddress?.validatedOn

    delete addressParams.undefined
    // const isCardPaymentType =
    //   paymentMethodType === 'CREDITCARD' || paymentMethodType === 'DEBITCARD'
    // const gpsPaymentOption = isCardPaymentType
    //   ? this.getConfiguredGPSPaymentOption()
    //   : null
    const gpsPaymentOption = this.getConfiguredGPSPaymentOption()
    const { type: paymentTypeForRequest, name: paymentNameForGpsType } =
      getGpsCreditCardPaymentIdentity(
        this.getGPSCreditCardOptions(),
        APPConfig.getAppConfig()?.gpsSavedCardProcessorName,
        paymentMethodType
      )

    const params = {
      type: paymentTypeForRequest,
      ...(paymentNameForGpsType && {
        name: paymentNameForGpsType,
      }),
      isDefault: setToDefaultPayment,
      isDefaultSubscriptionPayment: setDefaultSubscriptionPayment,
    }
    const name = getNamefromFirstname(cardFirstName) || ''
    if (
      paymentMethodType === 'CREDITCARD' ||
      paymentMethodType === 'DEBITCARD'
    ) {
      const expirationMonth = expiryDate?.split?.('/')?.[0] || '01'
      const expirationYear = expiryDate?.split?.('/')?.[1] || '20'
      const { firstName = '', lastName = '' } = name
      const { properties, provider: normalizedProvider } =
        getSubscriptionPaymentPayloadData({
          paymentContainer: this.getPaymentContainer(),
          paymetricContainer,
          provider,
          gpsPaymentOption,
        })
      params.accountInfo = {
        name: firstName,
        lastName,
        cardNumber,
        expiryMonth: expirationMonth,
        expiryYear: '20' + expirationYear,
        provider: normalizedProvider,
        isDefault: setToDefaultPayment,
      }
      params.billingAddress = selectedBillingAddress
      params.properties = properties
    }

    if (!billingSameAsShipping) {
      const transformedAddressFormData = addressParams || {}
      const modifiedAddressData = {
        ...transformedAddressFormData,
        default: true,
        street1: transformedAddressFormData?.addressLine1 || '',
        street2: transformedAddressFormData?.addressLine2 || '',
        zipCode:
          transformedAddressFormData?.zip ||
          transformedAddressFormData?.postalCode ||
          '',
      }
      delete modifiedAddressData.undefined
      this.handleAddNewPaymentContinue(addressParams, modifiedAddressData)
    } else {
      overlayState.showLoader()
      const addNewPaymentResponse = await customerContainer.addPayment(
        params,
        true,
        false
      )
      if (customerContainer.isSuccessResponse(addNewPaymentResponse)) {
        const addedPaymentId = customerContainer.lastPaymentResponse?.id || ''
        const {
          isGPS3DSEnabledInSubscription = false,
          triggerSubscription3DSInitiateCall = async () => null,
        } = this.props
        if (isGPS3DSEnabledInSubscription && addedPaymentId) {
          const initiateResponse =
            await triggerSubscription3DSInitiateCall(addedPaymentId)
          // A null response means 3DS did not apply, so the subscription still
          // has to be pointed at the new payment below.
          if (initiateResponse !== null) {
            overlayState.hideLoader()
            if (!initiateResponse?.redirectUrl) {
              this.handlePaymentModalToggleView()
            }
            return
          }
        }
        if (typeof window === 'object') {
          window.scrollTo(0, 0)
        }
        overlayState.hideLoader()
        if (params.isDefaultSubscriptionPayment || params.isDefault) {
          this.callAddNewSubscriptionPaymentHandler(addedPaymentId)
        }
        this.handlePaymentModalToggleView()
      } else {
        this.handlePaymentModalToggleView()
        overlayState.hideLoader()
      }
    }
  }

  handleAddNewPaymentContinue = async (formData = {}, reqBody = {}) => {
    const {
      isToMakeValidationApiCall = false,
      zip = '',
      postalCode = '',
    } = formData

    const {
      shippingInstructions = '',
      setDefaultAddress = false,
      country = '',
    } = reqBody

    const transformedFormData = {
      ...formData,
      zip: zip || postalCode || '',
      shippingInstructions,
      setDefaultAddress,
      country,
    }

    const isToMakeValidationApiForPayment =
      country === 'US' || country === 'CA' || isToMakeValidationApiCall

    let formFields = await this.getFormFieldsBasedOnCountry()

    if (isToMakeValidationApiForPayment) {
      const validationAddressResponse =
        await addressContainer.validateAddress(reqBody)
      const { code = '', address = {} } = validationAddressResponse
      const recommendationAddress =
        address?.validationDetails?.[0]?.recommendations || []

      if (code === 'ADCOVAL0003') {
        this.suggestionBillingAddressList = [{ ...transformedFormData }, []]
        this.isAddressInvalid = true
        this.isToShowSuggestionModal = true
      } else if (code == 'ONESOURCE_ADPLGIN_VAL_ERROR') {
        this.isToShowSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate(
            'payment.addPaymentFailure',
            'Sorry, we are unable to add your payment information. Please try again.'
          )
        )
        return
      } else if (
        addressContainer.isSuccessResponse(validationAddressResponse)
      ) {
        let firstValidationAddress = recommendationAddress?.[0] || {}
        let isFirstSuggestedAddressSameAsCurrentAddress =
          await checkIfTwoFormValuesAreEqual(
            formFields,
            transformedFormData,
            firstValidationAddress
          )

        if (
          Object.keys(firstValidationAddress)?.length > 0 &&
          !isFirstSuggestedAddressSameAsCurrentAddress
        ) {
          this.suggestionBillingAddressList = [
            { ...transformedFormData },
            ...recommendationAddress,
          ]
          this.isAddressInvalid = false
          this.isToShowSuggestionModal = true
        } else {
          this.handleAddressSuggestionModalContinue(transformedFormData)
        }
      } else {
        this.isToShowSuggestionModal = false
        toastState.setToastMessage(
          i18nTranslate(
            'payment.addPaymentFailure',
            'Sorry, we are unable to add your payment information. Please try again.'
          )
        )
        return
      }
    } else {
      this.handleAddressSuggestionModalContinue(transformedFormData)
    }
  }

  handleSavedPaymentContinue = () => {
    this.props.handleSavedPaymentContinue(this.selectedPaymentId)
    this.handlePaymentModalToggleView()
  }

  handlePaymentOptionChange = (paymentId = 0) => {
    this.selectedPaymentId = paymentId
  }

  handleSubscriptionPaymentTypeSelect = (type = 'CREDITCARD') => {
    this.selectedAddPaymentType = type
  }

  normalizePaymentMethodKey = (paymentMethodKey = '') =>
    paymentMethodKey.toLowerCase().replace(/[^a-z0-9]/g, '')

  handleGPSPaymentSaveChanges = async () => {
    const paymentType = (this.selectedAddPaymentType || '').toUpperCase()
    if (!paymentType) {
      return
    }

    this.isGPSPaymentSaveInProgress = true
    this.gpsSaveStatusMessage = ''
    overlayState.showLoader()
    const paymentTypeRequest =
      paymentType.toUpperCase() === 'WIRE' ? 'BANKTRANSFER' : paymentType
    const paymentName =
      paymentTypeRequest === 'BANKTRANSFER' ? 'Wire' : paymentTypeRequest
    // BE contract expects the resolved GPS name in the `type` field here
    const resolvedGpsPaymentType = resolveGpsPaymentName(
      paymentType?.toLowerCase(),
      paymentTypeRequest
    )
    // Wire is stored once per profile; reuse the existing BANKTRANSFER
    // paymentRefId instead of creating a duplicate (same as checkout).
    const savedBankTransferPaymentRefId = checkoutContainer.isBankTransferPaymentType(
      paymentName
    )
      ? checkoutContainer.getSavedBankTransferPaymentRefId()
      : ''
    try {
      const addPaymentResponse = await customerContainer.addPayment(
        {
          type: resolvedGpsPaymentType,
          isDefaultSubscriptionPayment: true,
          name: paymentName,
          properties: {
            displayName: paymentName,
          },
          ...(savedBankTransferPaymentRefId && {
            paymentItems: [{ paymentRefId: savedBankTransferPaymentRefId }],
          }),
          showInProfile: true,
        },
        true,
        false
      )

      if (customerContainer.isSuccessResponse(addPaymentResponse)) {
        const addedPaymentId = customerContainer.lastPaymentResponse?.id || ''
        this.gpsSaveStatusMessage = i18nTranslate(
          'payment.addSuccess',
          'Payment saved successfully.'
        )
        this.handlePaymentModalToggleView()
        if (addedPaymentId) {
          this.callAddNewSubscriptionPaymentHandler(addedPaymentId)
        } else {
          this.gpsSaveStatusMessage = i18nTranslate(
            'payment.attachError',
            'Payment saved but could not be linked. Please try again.'
          )
          toastState.setToastMessage(this.gpsSaveStatusMessage, false)
        }
      } else {
        this.gpsSaveStatusMessage = i18nTranslate(
          'payment.addError',
          'Failed to add payment. Please try again.'
        )
        toastState.setToastMessage(this.gpsSaveStatusMessage, false)
      }
    } catch (error) {
      console.error('Subscription GPS payment save failed', error)
      this.gpsSaveStatusMessage = i18nTranslate(
        'payment.addError',
        'Failed to add payment. Please try again.'
      )
      toastState.setToastMessage(this.gpsSaveStatusMessage, false)
    } finally {
      overlayState.hideLoader()
      this.isGPSPaymentSaveInProgress = false
    }
  }

  isSubscriptionDashboardPaymentMethodHidden = (
    paymentType = '',
    paymentDetails = []
  ) => {
    // The GPS response key is a content-managed label ('SEPA Direct Debit');
    // the option id carries the stable type the config is written against.
    const optionId = paymentDetails?.[0]?.id ?? ''
    const normalizedPaymentTypes = new Set(
      [paymentType, optionId]
        .filter(type => type !== '' && type !== null && type !== undefined)
        .map(type => this.normalizePaymentMethodKey(String(type)))
    )
    const hiddenPaymentMethods =
      APPConfig.getAppConfig()?.hideSubscriptionDashboardPaymentMethods || {}
    return Object.keys(hiddenPaymentMethods).some(
      type =>
        normalizedPaymentTypes.has(this.normalizePaymentMethodKey(type)) &&
        convertToBoolean(hiddenPaymentMethods[type])
    )
  }

  getSubscriptionPaymentOptions = () => {
    const enableGlobalPaymentSystem = isGPSEnabled()
    const isSuccessResponse = checkoutContainer.isSuccessResponse(
      checkoutContainer.globalPaymentOptionsListResponse
    )
    const gpsResponse = this.getGlobalPaymentResponse()
    const options = []
    if (
      enableGlobalPaymentSystem &&
      isSuccessResponse &&
      Object.keys(gpsResponse).length > 0
    ) {
      Object.keys(gpsResponse).forEach(paymentType => {
        const paymentDetails = gpsResponse[paymentType] || []
        if (
          Array.isArray(paymentDetails) &&
          paymentDetails.length > 0 &&
          !this.isSubscriptionDashboardPaymentMethodHidden(
            paymentType,
            paymentDetails
          )
        ) {
          const type = paymentType?.toLowerCase?.() || paymentType
          options.push({
            type,
            displayName:
              type === 'creditcard'
                ? i18nTranslate('checkout.creditCardText', 'Credit Card')
                : i18nTranslate(`checkout.${type}`, paymentType),

            dataTestId: `qa-sub-${type.toLowerCase()}-payment-option`,
            paymentId: `sub-paymentType-${type.toLowerCase()}-option`,
          })
        }
      })
    }
    return options
  }

  renderSubscriptionPaymentTypeOptions = () => {
    const paymentOptions = this.getSubscriptionPaymentOptions()
    if (!paymentOptions?.length) {
      return null
    }
    return (
      <fieldset
        className="subscription-payment-type-options mb-3"
        data-testid="qa-subscription-payment-type-options">
        <legend
          className="font-weight-bold mb-2"
          data-testid="qa-subscription-add-payment-methods-label">
          {i18nTranslate('checkout.addPaymentMethods', 'Add Payment Methods')}
        </legend>
        <div className="paymentTypeRadio subscription-payment-type-radio">
          {paymentOptions.map(option => {
            const { type, displayName, dataTestId, paymentId } = option
            return (
              <div
                key={type}
                className="subscription-payment-type-radio-wrapper">
                <input
                  type="radio"
                  name="subscriptionPaymentTypeRadios"
                  id={paymentId}
                  onChange={() =>
                    this.handleSubscriptionPaymentTypeSelect(type)
                  }
                  checked={this.selectedAddPaymentType === type}
                  className="payment-options"
                  data-testid={dataTestId}
                />
                <label
                  htmlFor={paymentId}
                  className="payment-label text-capitalize font-weight-bold ml-2">
                  {displayName}
                </label>
              </div>
            )
          })}
        </div>
      </fieldset>
    )
  }

  renderAddNewPaymentForm = () => {
    return (
      <>
        {this.renderSubscriptionPaymentTypeOptions()}
        {this.selectedAddPaymentType === 'creditcard' && (
          <PaymentMarketForm
            onSubmitForm={this.handleAddNewPaymentContinueButton}
            isPaymentEdit={true}
            isFromEditSubscription={true}
            includePaymetricCvv={false}
            billingAddressType="subscription"
            formType="subscription-market-form"
          />
        )}
        {this.selectedAddPaymentType &&
          this.selectedAddPaymentType !== 'creditcard' && (
          <div className="subscription-wire-save-btn-wrapper text-center">
            <Button
              variant="primary"
              data-testid={`qa-subscription-${this.selectedAddPaymentType}-save-changes`}
              className="subscription-wire-save-changes-btn w-100"
              disabled={this.isGPSPaymentSaveInProgress}
              onClick={this.handleGPSPaymentSaveChanges}>
              {i18nTranslate('checkout.saveChanges', 'Save Changes')}
            </Button>
            <div
              role="status"
              aria-live="polite"
              className="sr-only"
              data-testid="qa-gps-save-status-message">
              {this.gpsSaveStatusMessage}
            </div>
          </div>
        )}
      </>
    )
  }

  renderSavedPaymentTabButtons = () => {
    return (
      <>
        <Button
          variant="primary"
          data-testid="qa-saved-tab-close"
          className="saved-tab-cancel-button"
          onClick={this.handlePaymentModalToggleView}>
          {i18nTranslate('shipping.closeButton', 'Close')}
        </Button>
        <Button
          variant="primary"
          data-testid="qa-saved-tab-continue"
          className="saved-tab-continue-button"
          onClick={this.handleSavedPaymentContinue}>
          {i18nTranslate('shippingMethod.continue', 'Continue')}
        </Button>
      </>
    )
  }

  renderSepaIcon = () => {
    const sepaIconURL = getSepaIconURL()
    return sepaIconURL ? (
      <ImageComponent
        testId="qa-sepa-subscription-icon"
        src={sepaIconURL}
        name="SEPA"
        width="40px"
        height="25px"
        title="SEPA"
        aria-label="SEPA-icon"
        className="payment-icons"
        customParentClassName="mr-1 mb-1"
        overrideParentClass={true}
      />
    ) : (
      <PaymentIcon type="sepa" />
    )
  }

  renderPaymentMethod = (payment = {}) => {
    const {
      name = '',
      firstName = '',
      lastName = '',
      cardExpirationYear = '',
      cardExpirationMonth = '',
      expiryYear = '',
      expiryMonth = '',
      type = '',
      provider = '',
      id = '',
      isMandateInProgress = false,
    } = payment
    const isWirePayment = (type || '').toUpperCase() === 'BANKTRANSFER'
    // Records arrive as name 'Twikey'/'SEPA' or as type 'SEPA', so both are checked.
    const paymentDisplayName = isSepaPaymentRecord({
      name: payment?.displayName,
      type,
    })
    const sepaToken =
      payment?.token ||
      payment?.iban ||
      payment?.IBAN ||
      payment?.properties?.iban ||
      ''
    const cardNumber = paymentDisplayName
      ? sepaToken.slice(-4)
      : (payment?.cardNumber || '')?.replace(
          /^(.{4})(.{4})(.{4})(.*)$/,
          '$1-$2-$3-$4'
        )
    const expiryText = i18nTranslate('payment.cardExpiresText', 'Expires')
    const expiredText = i18nTranslate(
      'payment.cardExpiredText',
      'Card is Expired'
    )

    const daysInExpirationMonth = dayjs(
      `${parseInt(cardExpirationYear || expiryYear)}-${parseInt(
        cardExpirationMonth || expiryMonth
      )}-01`
    ).daysInMonth()

    const cardExpirationDate = dayjs(
      `${parseInt(cardExpirationYear || expiryYear)}-${parseInt(
        cardExpirationMonth || expiryMonth
      )}-${daysInExpirationMonth}`
    )

    const daysLeftForExpiry = cardExpirationDate.diff(
      dayjs().format('YYYY-MM-DD'),
      'day'
    )
    let isCardExpiringSoon = daysLeftForExpiry <= 60 && daysLeftForExpiry >= 0
    let isCardExpired = daysLeftForExpiry < 0
    const endingText = '**'
    const cardFirstName = firstName || name
    const editButtonText = isCardExpired
      ? i18nTranslate('catalog.Update', 'Update', {
          nameSpace: 'ssr-resource',
        })
      : i18nTranslate('edit', 'Edit')

    if (isWirePayment) {
      return (
        <Col className={`p-0`} data-testid="qa-user-sub-payment-method">
          <div className="p-0 text-dark paragraph-s m-0 shipping-text">
            <span className="subscription-payment-details">
              <span>{name}</span>
            </span>
            <br />
            <div className="subscription-card-button-wrapper">
              <span>
                <Button
                  variant="link"
                  className="subscription-card-link-btn p-0"
                  data-testid="qa-subscription-delete-payment"
                  aria-label={`${i18nTranslate('cart.delete', 'Delete')} ${name}`}
                  onClick={e => {
                    this.showDeleteConfirmationModal(payment?.id)
                  }}>
                  {i18nTranslate('cart.delete', 'Delete')}
                </Button>
              </span>
            </div>
          </div>
        </Col>
      )
    }

    return (
      <Col className={`p-0`} data-testid="qa-user-sub-payment-method">
        <div className="p-0 text-dark paragraph-s m-0 shipping-text">
          {!paymentDisplayName && name !== null && `${cardFirstName} `}
          {!paymentDisplayName &&
            cardFirstName !== lastName &&
            lastName !== null &&
            `${lastName} `}
          {!paymentDisplayName &&
            (cardFirstName !== null || lastName !== null) && <br />}
          <span className="subscription-payment-details">
            {paymentDisplayName ? (
              this.renderSepaIcon()
            ) : (
              <PaymentIcon type={provider?.toLowerCase()} />
            )}
            <span>
              {paymentDisplayName
                ? i18nTranslate(
                    'payment.sepaDirectDebitEndingIn',
                    'Sepa Direct Debit ending in '
                  )
                : provider}
            </span>
            <span>
              {!paymentDisplayName && endingText}
              {cardNumber}
            </span>
          </span>
          {!paymentDisplayName && (
            <span
              data-testid="qa-expiry-date"
              className={`paragraph-m payment-card-expiry ${
                isCardExpired ? 'payment-card-expired' : ''
              } `}>
              {`${isCardExpired ? expiredText : expiryText} ${
                cardExpirationMonth || expiryMonth
              }/${String(cardExpirationYear || expiryYear)?.substr(2)}`}
            </span>
          )}
          {isCardExpired && (
            <CreditCardExpiryInfo
              isCardExpired={isCardExpired}
              isCardExpiringSoon={isCardExpiringSoon}
              cardType={type}
            />
          )}
          <br />
          {isMandateInProgress &&
            this.mandateWarningPaymentId === String(id) && (
              <SepaMandateWarningBanner id={id} />
            )}
          <div className="subscription-card-button-wrapper">
            {!paymentDisplayName && (
              <span>
                <Button
                  variant="link"
                  className="subscription-card-link-btn p-0"
                  data-testid="qa-subscription-edit-payment"
                  aria-label={`${i18nTranslate('cart.edit', 'Edit')} ${name}`}
                  onClick={() => {
                    this.handlePaymentEditButton(payment?.id)
                  }}>
                  {editButtonText}
                </Button>
              </span>
            )}
            {!isMandateInProgress && (
              <span>
                <Button
                  variant="link"
                  className="subscription-card-link-btn p-0"
                  data-testid="qa-subscription-delete-payment"
                  aria-label={`${i18nTranslate('cart.delete', 'Delete')} ${name}`}
                  onClick={e => {
                    this.showDeleteConfirmationModal(payment?.id)
                  }}>
                  {i18nTranslate('cart.delete', 'Delete')}
                </Button>
              </span>
            )}
          </div>
        </div>
      </Col>
    )
  }

  renderSavedPaymentRadioOption = (payment = {}) => {
    const { id = 0 } = payment
    const { accountInfo = {}, properties = {} } = payment
    const paymentInfo = toJS(accountInfo || {})
    paymentInfo.properties = properties || {}
    paymentInfo.type = paymentInfo?.type || payment?.type || ''
    paymentInfo.displayName = payment?.name || ''
    // The dashboard payment id must win over accountInfo.id, otherwise
    // delete/edit/mandate-matching act on the wrong record.
    paymentInfo.id = id
    paymentInfo.name = paymentInfo.name || payment?.name || ''
    const isMandateInProgress = isSepaMandateInProgress(payment)
    paymentInfo.isMandateInProgress = isMandateInProgress

    return (
      <>
        <input
          type="radio"
          name="paymentOption"
          aria-label={id}
          key={'option' + id}
          id={'option' + id}
          onChange={() => {
            this.handlePaymentOptionChange(id)
          }}
          checked={this.selectedPaymentId == id}
          disabled={isMandateInProgress}
          data-testid="qa-subscription-saved-payment-option"
        />
        <label
          htmlFor={'option' + id}
          className={`subscription-payment-details-wrapper mb-3 ${
            isMandateInProgress
              ? 'subscription-payment-details-wrapper--readonly'
              : ''
          }`}
          onMouseEnter={() =>
            isMandateInProgress && (this.mandateWarningPaymentId = String(id))
          }
          onMouseLeave={() =>
            this.mandateWarningPaymentId === String(id) &&
            (this.mandateWarningPaymentId = '')
          }
          onFocus={() =>
            isMandateInProgress && (this.mandateWarningPaymentId = String(id))
          }
          onBlur={() =>
            this.mandateWarningPaymentId === String(id) &&
            (this.mandateWarningPaymentId = '')
          }>
          {this.renderPaymentMethod(paymentInfo)}
        </label>
      </>
    )
  }

  renderSavedPaymentTabDetails = () => {
    const paymentData = customerContainer.paymentsForDashboard?.payments || []
    if (paymentData?.length) {
      return (
        <div className="subscription-saved-payment-tab-content">
          {paymentData?.map((payment, index) => {
            return (
              <React.Fragment key={payment?.id ?? index}>
                {this.renderSavedPaymentRadioOption(payment)}
              </React.Fragment>
            )
          })}
        </div>
      )
    }
    return <p>Payment</p>
  }

  renderTabView = () => {
    const { firstTabName = '', secondTabName = '' } = this.props
    return (
      <Tabs
        activeKey={this.activeTab}
        onSelect={tabName => this.handlePaymentTabChange(tabName)}
        id="subscription-payment-tab"
        unmountOnExit={true}
        className="subscription-payment-tabs-wrapper"
        data-testid="qa-subscription-payment-tab-wrapper"
        fill>
        <Tab
          eventKey="savedDetailsTab"
          data-testid="qa-subscription-saved-payment-tab"
          className="subscription-saved-payment-tab-wrapper"
          title={firstTabName}>
          {this.renderSavedPaymentTabDetails()}
          <Modal.Footer>{this.renderSavedPaymentTabButtons()}</Modal.Footer>
        </Tab>
        <Tab
          eventKey="addNewDetailsTab"
          data-testid="qa-subscription-add-payment-form"
          className="subscription-add-payment-tab-wrapper mb-3"
          title={secondTabName}>
          {this.renderAddNewPaymentForm()}
        </Tab>
      </Tabs>
    )
  }

  toggleSubscriptionPaymentEditModal = () => {
    this.isToShowPaymentEditModal = false
  }

  render() {
    const {
      customModalTitle = '',
      isToShowPaymentModal = false,
      selectedAddressId = '',
      selectedPaymentId = '',
      handleEditSubscriptionPayment,
    } = this.props
    const paymentData = customerContainer.paymentsForDashboard?.payments || []
    const addressData = addressContainer.addressData || []

    return (
      <>
        <Modal
          aria-hidden={false}
          centered
          data-testid="qa-multiple-subscription-payment-modal"
          show={isToShowPaymentModal && !this.isToShowPaymentEditModal}
          key="payment-edit-modal"
          className="subscription-payment-edit-modal"
          onHide={this.handlePaymentModalToggleView}>
          <Modal.Header closeButton>
            <Modal.Title
              as="h4"
              className="mb-0 subscription-payment-edit-modal-title">
              {customModalTitle}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="subscription-payment-edit-modal-body">
            {this.renderTabView()}
          </Modal.Body>
        </Modal>
        {this.isToShowSuggestionModal && (
          <AddressValidation
            open={this.isToShowSuggestionModal}
            addressInvalidFlag={this.isAddressInvalid}
            recommendations={this.suggestionBillingAddressList}
            handleClose={this.handleSuggestionModalClose}
            handleClickOnPro={this.handleAddressSuggestionModalContinue}
          />
        )}
        {this.isToShowDeleteConfirmationModal && (
          <PaymentDeleteModal
            isToShowDeleteModal={this.isToShowDeleteConfirmationModal}
            toggleDeleteConfirmationModal={
              this.handleDeleteConfirmationModalClose
            }
            selectedPaymentForDelete={this.selectedPaymentForDelete}
          />
        )}
        {this.isToShowPaymentEditModal && (
          <SubscriptionPaymentEditModal
            data-testid="qa-subscription-modal"
            toggleSubscriptionPaymentEditModal={
              this.toggleSubscriptionPaymentEditModal
            }
            isToShowPaymentEditModal={this.isToShowPaymentEditModal}
            paymentData={paymentData}
            customModalTitle={i18nTranslate(
              'checkout.updatePayment',
              'Update your payment information'
            )}
            selectedPaymentData={this.selectedPaymentDataToEdit}
            selectedAddressId={selectedAddressId}
            subscriptionPaymentId={selectedPaymentId}
            addressData={addressData}
            handleEditSubscriptionPayment={handleEditSubscriptionPayment}
          />
        )}
      </>
    )
  }
}

export { SubscriptionPaymentModal }
export default SubscriptionPaymentModal
